import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { SitePermitStatus } from '~/client/graph'
import { ArchiveIcon } from '~/client/src/shared/components/icons/ArchiveIcon'
import { CheckDoneIcon } from '~/client/src/shared/components/icons/CheckDoneIcon'
import { ClearDayIcon } from '~/client/src/shared/components/icons/ClearDayIcon'
import { EmailIcon } from '~/client/src/shared/components/icons/EmailIcon'
import { UnarchiveIcon } from '~/client/src/shared/components/icons/UnarchiveIcon'
import { UnreadIcon } from '~/client/src/shared/components/icons/UnreadIcon'
import { UserFilledIcon } from '~/client/src/shared/components/icons/UserFilledIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import StatusUpdateMenu from '~/client/src/shared/components/StatusUpdateMenu/StatusUpdateMenu'
import { clearSelection as clearListSelection } from '~/client/src/shared/lists/core'
import { NotificationsListStore } from '~/client/src/shared/lists/notifications/NotificationsList.store'
import { toggleAllFilteredSelection } from '~/client/src/shared/lists/toggleAllFilteredSelection'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import BaseNotification from '~/client/src/shared/models/Notification'
import Workflow from '~/client/src/shared/models/Workflow'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import {
  archive,
  BulkActionsDeps,
  bulkApprove,
  canMarkAsDone,
  confirmStatusUpdate,
  getApprovableUsers,
  getCommonStepItemsForSelected,
  getIsAdmin,
  getSelectedPermits,
  hasPermitNotificationsSelected,
  hasReadSelected,
  hasUnreadSelected,
  isAllSelected,
  isSomeSelected,
  markAsRead,
  markAsUnread,
  unarchive,
} from '~/client/src/shared/utils/notificationBulkActions'
import { IStatusItem } from '~/client/src/shared/utils/permitStatusUtils'

import DesktopEventStore from '../../../stores/EventStore/DesktopEvents.store'
import ConfirmNotificationStatusUpdateDialog from './ConfirmNotificationStatusUpdateDialog'

interface IProps {
  notificationsListStore: NotificationsListStore
  workflowTypesStore?: WorkflowTypesStore
  eventsStore?: DesktopEventStore
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
}

@inject(
  'eventsStore',
  'workflowTypesStore',
  'projectMembersStore',
  'userProjectsStore',
)
@observer
export default class NotificationBulkActionsBar extends React.Component<IProps> {
  @observable private pendingItem: IStatusItem | null = null
  @observable private pendingPermits: Workflow[] = []
  @observable private isConfirmDialogOpen: boolean = false
  @observable private isStatusMenuOpen: boolean = false

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
  }

  public componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  public render() {
    const { notificationsListStore } = this.props
    const filteredNotifications = this.filteredNotifications
    const selectedNotifications = this.selectedNotifications
    const count = notificationsListStore.list.selection.count
    const allSelected = isAllSelected(
      filteredNotifications,
      selectedNotifications,
    )
    const someSelected = isSomeSelected(
      filteredNotifications,
      selectedNotifications,
    )
    const adminWithPermits =
      getIsAdmin(this.props.eventsStore) &&
      hasPermitNotificationsSelected(selectedNotifications)

    return (
      <>
        <div className="tw:flex tw:items-center tw:w-full tw:bg-background tw:border-b tw:border-border tw:py-1.5 tw:pl-3.5 tw:gap-2 tw:min-h-[45px]">
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onCheckedChange={() => this.handleToggleSelectAll()}
          />

          {count > 0 ? (
            <>
              <span className="tw:text-sm tw:font-medium tw:text-muted-foreground tw:whitespace-nowrap">
                {count} {Localization.translator.notifications}{' '}
                {Localization.translator.selected.toLowerCase()}
              </span>

              <div className="tw:flex tw:items-center tw:h-8 tw:py-0.5 tw:flex-shrink-0">
                <div className="tw:w-px tw:h-full tw:bg-border" />
              </div>

              <Button
                variant="ghost"
                disabled={!hasUnreadSelected(selectedNotifications)}
                onClick={this.handleMarkAsRead}
              >
                <EmailIcon size="sm" data-icon="inline-start" />
                {Localization.translator.read}
              </Button>

              <Button
                variant="ghost"
                disabled={!hasReadSelected(selectedNotifications)}
                onClick={this.handleMarkAsUnread}
              >
                <UnreadIcon size="sm" data-icon="inline-start" />
                {Localization.translator.unread}
              </Button>

              <Button
                variant="ghost"
                disabled={
                  !(adminWithPermits && canMarkAsDone(selectedNotifications))
                }
                onClick={this.handleDone}
              >
                <CheckDoneIcon size="md" data-icon="inline-start" />
                {Localization.translator.done}
              </Button>

              <Popover
                open={this.isStatusMenuOpen}
                onOpenChange={this.setStatusMenuOpen}
              >
                <PopoverTrigger
                  disabled={!adminWithPermits}
                  render={<Button variant="ghost" />}
                >
                  <ClearDayIcon size="md" data-icon="inline-start" />
                  {Localization.translator.updateStatus}
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  className="tw:w-auto"
                >
                  <StatusUpdateMenu
                    items={getCommonStepItemsForSelected(
                      selectedNotifications,
                      this.props.workflowTypesStore,
                    )}
                    title={
                      Localization.translator.updateSelectedWorkflowsStatusTo
                    }
                    onStatusSelected={this.handleStatusSelected}
                    onClose={this.closeStatusMenu}
                  />
                </PopoverContent>
              </Popover>

              <Button
                variant="ghost"
                disabled={
                  !(
                    getApprovableUsers(
                      selectedNotifications,
                      this.props.projectMembersStore,
                      this.props.userProjectsStore,
                    ).length > 0
                  )
                }
                onClick={this.handleBulkApprove}
              >
                <UserFilledIcon
                  size="md"
                  className="tw:text-blue-600"
                  data-icon="inline-start"
                />
                {Localization.translator.approve_verb}
              </Button>

              {notificationsListStore.isArchiveFilterActive ? (
                <Button variant="ghost" onClick={this.handleUnarchive}>
                  <UnarchiveIcon size="sm" data-icon="inline-start" />
                  {Localization.translator.unArchive}
                </Button>
              ) : (
                <Button variant="ghost" onClick={this.handleArchive}>
                  <ArchiveIcon size="sm" data-icon="inline-start" />
                  {Localization.translator.archive}
                </Button>
              )}
            </>
          ) : (
            <span className="tw:text-sm tw:font-medium tw:text-muted-foreground tw:whitespace-nowrap">
              {filteredNotifications.length}{' '}
              {Localization.translator.notifications}
            </span>
          )}
        </div>
        <ConfirmNotificationStatusUpdateDialog
          isOpen={this.isConfirmDialogOpen}
          selectedItem={this.pendingItem}
          permitCount={this.pendingPermits.length}
          onConfirm={this.handleConfirmStatusUpdate}
          onCancel={this.handleCancelStatusUpdate}
        />
      </>
    )
  }

  private get deps(): BulkActionsDeps {
    return {
      eventsStore: this.props.eventsStore,
      permitTypesStore: this.props.workflowTypesStore,
      projectMembersStore: this.props.projectMembersStore,
      userProjectsStore: this.props.userProjectsStore,
      onAfterAction: this.clearSelection,
    }
  }

  private handleToggleSelectAll = () => {
    const list = this.props.notificationsListStore.list
    toggleAllFilteredSelection(list.selection, action => list.dispatch(action))
  }

  private handleMarkAsRead = () =>
    markAsRead(this.deps, this.selectedNotifications)
  private handleMarkAsUnread = () =>
    markAsUnread(this.deps, this.selectedNotifications)
  private handleArchive = () => archive(this.deps, this.selectedNotifications)
  private handleUnarchive = () =>
    unarchive(this.deps, this.selectedNotifications)
  private handleBulkApprove = () =>
    bulkApprove(this.deps, this.selectedNotifications)

  @action.bound
  private handleDone() {
    this.pendingPermits = getSelectedPermits(this.selectedNotifications)
    this.pendingItem = {
      status: SitePermitStatus.Done,
      workflowStepLevel: 0,
    }
    this.isConfirmDialogOpen = true
  }

  @action.bound
  private setStatusMenuOpen(isOpen: boolean) {
    this.isStatusMenuOpen = isOpen
  }

  private closeStatusMenu = () => this.setStatusMenuOpen(false)

  @action.bound
  private handleStatusSelected(item: IStatusItem) {
    this.isStatusMenuOpen = false
    this.pendingPermits = getSelectedPermits(this.selectedNotifications)
    this.pendingItem = item
    this.isConfirmDialogOpen = true
  }

  @action.bound
  private handleConfirmStatusUpdate() {
    confirmStatusUpdate(this.deps, this.pendingItem, this.pendingPermits)
    this.isConfirmDialogOpen = false
    this.pendingItem = null
    this.pendingPermits = []
  }

  @action.bound
  private handleCancelStatusUpdate() {
    this.isConfirmDialogOpen = false
    this.pendingItem = null
    this.pendingPermits = []
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    const tag = (event.target as HTMLElement)?.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
    if ((event.target as HTMLElement)?.isContentEditable) return

    if (event.key === 'e' || event.key === 'E') {
      const count = this.props.notificationsListStore.list.selection.count
      if (count === 0) return

      if (this.props.notificationsListStore.isArchiveFilterActive) {
        this.handleUnarchive()
      } else {
        this.handleArchive()
      }
    }
  }

  private get filteredNotifications(): BaseNotification[] {
    return this.props.notificationsListStore.list.filteredRecords.map(
      row => row.notification,
    )
  }

  private get selectedNotifications(): BaseNotification[] {
    const { list } = this.props.notificationsListStore
    return list.inputRecords
      .filter(row => list.selection.selectedIds.has(row.id))
      .map(row => row.notification)
  }

  @action.bound
  private clearSelection() {
    this.props.notificationsListStore.list.dispatch(clearListSelection())
  }
}
